import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import pushRoute from '@utils/pushRoute';
import textHelper from '@utils/textHelper';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import Tags from '@base/Tags/Tags';
import P1 from '@base/P1/P1';
import PriceHike from '@comp/PriceHike/PriceHike';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import { CHECKOUT_STARTED } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { Spacings } from '@styles/index';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import usePriceHikeInfo from '@hooks/usePriceHikeInfo';
import useWindowSize from '@hooks/useWindowSize';

const Container = styled.section`
  position: relative;
  margin: auto;
  margin-top: 12px;
  border-radius: 8px;
  width: 1136px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (max-width: 70.95em) {
    width: 728px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    padding: 0 16px;
    margin-top: 16px;
  }
`;

const PriceHikeContainer = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  width: 892px;
  @media only screen and (max-width: 70.95em) {
    width: 728px;
  }
  @media only screen and (max-width: 47.95em) {
    width: 100%;
  }
`;

const Label = styled(H2)`
  font-size: 40px;
  @media (max-width: 47.95em) {
    font-size: 25px;
  }
`;

const Row = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  margin-top: ${Spacings.SPACING_16B};
  @media (max-width: 47.95em) {
    flex-direction: column;
    margin-top: 34px;
  }
`;

const SubscriptionBlock = styled(Paper)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 0px;
  width: 211px;
  height: 286px;
  padding-bottom: ${Spacings.SPACING_6B};
  :not(:last-child) {
    margin-right: ${Spacings.SPACING_4B};
  }
  @media (max-width: 70.95em) {
    width: 25%;
    height: 206px;
    padding-bottom: 0;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    padding: 16px;
    position: relative;
    margin-bottom: 24px;
    border: 1px solid var(--color-divider);
    box-sizing: border-box;
    height: 84px;
    justify-content: center;
    :not(:last-child) {
      margin-right: 0;
    }
  }
`;

const DiscountTags = styled(Tags)`
  margin-bottom: ${Spacings.SPACING_4B};
  @media (max-width: 47.95em) {
    position: absolute;
    right: 16px;
    top: -12px;
    background: var(--color-i-orange-4);
  }
`;

const Duration = styled(H4)`
  @media (max-width: 70.95em) {
    font-size: 14px;
    line-height: 100%;
  }
  @media (max-width: 47.95em) {
    font-size: 18px;
  }
`;

const Price = styled(H2)`
  margin-top: ${Spacings.SPACING_2B};
  @media (max-width: 70.95em) {
    font-size: 22px;
  }
  @media (max-width: 47.95em) {
    font-size: 28px;
    margin-top: 0;
  }
`;

const Info = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  color: var(--color-text-secondary);
  @media (max-width: 70.95em) {
    font-size: 12px;
    margin-top: 17px;
  }
  @media (max-width: 47.95em) {
    margin-top: 0;
  }
`;

const SubscribeButton = styled(Button)`
  margin-top: ${Spacings.SPACING_6B};
  @media (max-width: 70.95em) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    width: 100%;
    font-size: 14px;
    margin-top: 12px;
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Wrapper = styled.div`
  text-align: center;
  @media (max-width: 47.95em) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const ViewPlans = styled(Button)`
  width: 100%;
  @media (min-width: 47.95em) {
    display: none;
  }
`;

const SubscriptionsSection = ({
  block,
  goalUID,
  goalSlug,
  goalDetails = {},
  isSubscribed
}) => {
  const purchaseType = useContext(PurchaseTypeContext);
  const { data } = block;
  const { name } = goalDetails;
  const { isMobile } = useWindowSize();
  const handlePricingClick = (dataItem) => {
    const { uid } = dataItem;
    segment.track(
      CHECKOUT_STARTED,
      {
        purchase_type: purchaseType,
        goal_uid: goalUID,
        goal_name: name,
        is_subscription_active: false,
        is_upgrade: false,
        last_primary_source: segmentStorage.getItem('lps')
      },
      evtNameCategoryMap.BOTH
    );
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      `/goal/${goalSlug}/${goalUID}/subscribe/${uid}`,
      {},
      'goal'
    );
  };
  const handleMobilePricingClick = (dataItem) => {
    if (isMobile) {
      handlePricingClick(dataItem);
    }
  };
  const onViewAllPlansClick = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe/`,
      {},
      'goal'
    );
  };

  const CTALabel = usePurchaseCTAHandler(goalUID);

  const priceHikeInfo = usePriceHikeInfo(goalUID);

  return isSubscribed ? null : (
    <Container>
      <Label>{`${name || ''} Subscription`}</Label>
      <Row>
        {data.slice(0, 4).map((dataItem, index) => {
          const displayData = dataItem?.displayData;
          const currency = textHelper.currencySymbolMap('INR');
          const actualAmount = parseInt(dataItem.price, 10);
          const actualAmountText = textHelper.commas(actualAmount);

          return (
            <SubscriptionBlock
              key={String(index)}
              onClick={() => handleMobilePricingClick(dataItem)}
            >
              {displayData?.percentLess && (
                <DiscountTags type="discount">
                  {displayData?.percentLess}
                </DiscountTags>
              )}
              <Wrapper>
                <Duration>{displayData?.displayDuration}</Duration>
                <div>
                  <Price>{`${currency}${actualAmountText}`}</Price>
                  <Info>Total (Incl. of all taxes)</Info>
                </div>
              </Wrapper>
              <SubscribeButton
                dataAnalytics="goal-getSubscription"
                variant={dataItem.isDefault ? 'filled' : 'hollow'}
                label={CTALabel}
                onClick={() => handlePricingClick(dataItem)}
              />
            </SubscriptionBlock>
          );
        })}
      </Row>
      <ViewPlans
        variant="filled"
        label="View all plans"
        onClick={onViewAllPlansClick}
      />
      {priceHikeInfo && (
        <PriceHikeContainer>
          <PriceHike
            iconURL={priceHikeInfo?.icon}
            message={priceHikeInfo?.price_hike_text}
          />
        </PriceHikeContainer>
      )}
    </Container>
  );
};

SubscriptionsSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  goalDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  isSubscribed: PropTypes.bool.isRequired
};

export default SubscriptionsSection;
